/**
 * Stage 04 — Templates section for the Atomic Design showcase.
 * Dashboard (T-01), List+Detail (T-02), Form/Wizard (T-03),
 * Detail (T-04), Admin index (T-05).
 */

import type { CSSProperties } from 'react'
import { Grid2, PrimitiveCard, SectionHeader, TmplBar, TmplBlock } from './shared'

/** Reusable wrapper for template skeleton cards. */
function SkeletonWrapper({ children }: { children: React.ReactNode }) {
  const style: CSSProperties = {
    width: '100%',
    border: '1px solid var(--border)',
    borderRadius: 14,
    background: 'var(--surface)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  }
  return <div style={style}>{children}</div>
}

export function TemplatesSection() {
  return (
    <section id="templates" style={{ marginBottom: 80, scrollMarginTop: 24 }}>
      <SectionHeader
        num="04"
        name="Templates"
        tag="Page skeletons"
        stage="templates"
        desc="The wireframe layer — organisms arranged in a layout, with no real content. Five templates cover every screen in Lumen."
      />

      <Grid2>
        {/* T-01 Dashboard */}
        <PrimitiveCard
          name="Dashboard"
          tag="Template"
          desc="Sidebar + topbar + stat row + main grid + AI rail. The default shape for Home and HR Overview."
        >
          <SkeletonWrapper>
            <TmplBar label="T-01 · Dashboard" />
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '60px 1fr 200px',
                gridTemplateRows: '30px 60px 1fr',
                gap: 1,
                background: 'var(--border)',
                minHeight: 260,
              }}
            >
              <TmplBlock label="Side" style={{ gridRow: 'span 3' }} />
              <TmplBlock label="Top bar" style={{ gridColumn: 'span 2' }} />
              <TmplBlock label="Stats × 4" type="accent" />
              <TmplBlock label="ILMU rail" type="dark" />
              <TmplBlock label="Main content" type="fill" />
              <TmplBlock label="Activity" />
            </div>
          </SkeletonWrapper>
        </PrimitiveCard>

        {/* T-02 List + Detail */}
        <PrimitiveCard
          name="List + Detail"
          tag="Template"
          desc="Browse list on the left, focused detail on the right. Used for Approvals, Escalations."
        >
          <SkeletonWrapper>
            <TmplBar label="T-02 · List + Detail" />
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '60px 280px 1fr',
                gridTemplateRows: '30px 1fr',
                gap: 1,
                background: 'var(--border)',
                minHeight: 260,
              }}
            >
              <TmplBlock label="Side" style={{ gridRow: 'span 2' }} />
              <TmplBlock label="Top bar" style={{ gridColumn: 'span 2' }} />
              <TmplBlock label="List · filters" />
              <TmplBlock label="Detail panel" type="fill" />
            </div>
          </SkeletonWrapper>
        </PrimitiveCard>

        {/* T-03 Form / Wizard */}
        <PrimitiveCard
          name="Form / Wizard"
          tag="Template"
          desc="Centered single-column flow. Apply-for-leave, conflict-resolve."
        >
          <SkeletonWrapper>
            <TmplBar label="T-03 · Form" />
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '60px 1fr 220px',
                gridTemplateRows: '30px 40px 1fr 40px',
                gap: 1,
                background: 'var(--border)',
                minHeight: 260,
              }}
            >
              <TmplBlock label="Side" style={{ gridRow: 'span 4' }} />
              <TmplBlock label="Top bar" style={{ gridColumn: 'span 2' }} />
              <TmplBlock label="Stepper" style={{ gridColumn: 'span 2' }} />
              <TmplBlock label="Form fields" type="fill" />
              <TmplBlock label="ILMU advice" type="dark" />
              <TmplBlock label="Actions" style={{ gridColumn: 'span 2' }} />
            </div>
          </SkeletonWrapper>
        </PrimitiveCard>

        {/* T-04 Detail */}
        <PrimitiveCard
          name="Detail (single-record)"
          tag="Template"
          desc="Profile, request, or pattern viewed in full. Right rail for context."
        >
          <SkeletonWrapper>
            <TmplBar label="T-04 · Detail" />
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '60px 1fr 240px',
                gridTemplateRows: '30px 90px 1fr',
                gap: 1,
                background: 'var(--border)',
                minHeight: 260,
              }}
            >
              <TmplBlock label="Side" style={{ gridRow: 'span 3' }} />
              <TmplBlock label="Top bar" style={{ gridColumn: 'span 2' }} />
              <TmplBlock label="Hero summary" type="accent" />
              <TmplBlock label="Right rail" />
              <TmplBlock label="Tabs + body" type="fill" />
              <TmplBlock label="Timeline / AI" type="dark" />
            </div>
          </SkeletonWrapper>
        </PrimitiveCard>

        {/* T-05 Admin index */}
        <PrimitiveCard
          name="Admin index"
          tag="Template"
          desc="Filter row over a long table. Used for HR Employees, Audit, Credits."
        >
          <SkeletonWrapper>
            <TmplBar label="T-05 · Admin index" />
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '60px 1fr',
                gridTemplateRows: '30px 40px 36px 1fr',
                gap: 1,
                background: 'var(--border)',
                minHeight: 260,
              }}
            >
              <TmplBlock label="Side" style={{ gridRow: 'span 4' }} />
              <TmplBlock label="Top bar" />
              <TmplBlock label="Title + actions" type="accent" />
              <TmplBlock label="Filters · search" />
              <TmplBlock label="Table — n rows" type="fill" />
            </div>
          </SkeletonWrapper>
        </PrimitiveCard>
      </Grid2>
    </section>
  )
}
